import { JsonApiDataInterface } from './data.interface';
import { JsonApiPayloadInterface } from './payload.interface';
import { JsonApiModelInterface } from './model.interface';

/**
 * Fork of beauby/jsonapi-datastore@0.4.0-beta @JsonApiDataStoreModel
 *
 * Unfortunately, a TypeScript class cannot extend a JavaScript ES6 class,
 * so we had to fork & rewrite the library in TypeScript.
 *
 * @url https://github.com/beauby/jsonapi-datastore/
 */
export class JsonApiModel implements JsonApiModelInterface {
  /**
   * JSON API resource type (must match the backend API resource type)
   */
  public static readonly JSONAPI_TYPE: string = '__generic__';

  /**
   * @inheritDoc
   */
  public jsonApiType: string = JsonApiModel.JSONAPI_TYPE;

  /**
   * @inheritDoc
   */
  public jsonApiAttributes: Array<keyof this> = [];

  /**
   * @inheritDoc
   */
  public jsonApiRelationships: Array<keyof this> = [];

  /**
   * @inheritDoc
   */
  public jsonApiLinks: string[] = [];

  /**
   * @inheritDoc
   */
  public jsonApiMeta: string[] = [];

  /**
   * @inheritDoc
   */
  public links: { [key: string]: any } | undefined = {};

  /**
   * @inheritDoc
   */
  public meta: { [key: string]: any } | undefined = {};

  /**
   * @inheritDoc
   */
  public id: string = '';

  /**
   * @inheritDoc
   */
  public serialize(
    attributes?: Array<keyof this> | undefined,
    relationships?: Array<keyof this> | undefined,
    links?: string[] | undefined,
    meta?: string[] | undefined
  ): JsonApiPayloadInterface {
    const data: JsonApiDataInterface = {
      type: this.jsonApiType,
    };

    const payload: JsonApiPayloadInterface = {
      data: data,
    };

    if (attributes === undefined) {
      attributes = this.jsonApiAttributes;
    }

    if (relationships === undefined) {
      relationships = this.jsonApiRelationships;
    }

    if (links === undefined) {
      links = this.jsonApiLinks;
    }

    if (meta === undefined) {
      meta = this.jsonApiMeta;
    }

    if (this.id) {
      data.id = this.id;
    }

    if (attributes.length !== 0) {
      data.attributes = {};
    }

    if (relationships.length !== 0) {
      data.relationships = {};
    }

    if (links.length !== 0) {
      data.links = {};
    }

    if (meta.length !== 0) {
      data.meta = {};
    }

    // Attributes
    attributes.forEach((key) => (data.attributes[key as string] = this[key]));

    // Links
    links.forEach((key: string) => (data.links[key] = this.links[key] || ''));

    // Meta
    meta.forEach((key: string) => (data.meta[key] = this.meta[key] || ''));

    // Relationships
    relationships.forEach((key) => {
      // Relationship does not exist
      if (!this[key]) {
        data.relationships[key as string] = {
          data: null,
        };

        // Relationship is an array
      } else if ((<any>this[key]).constructor === Array) {
        // Using 'any' because we have an array of 'anything'
        data.relationships[key as string] = {
          data: (<any>this[key]).map((model: JsonApiModel) => {
            return {
              type: model.jsonApiType,
              id: model.id,
            };
          }),
        };

        // Relationship is an object
      } else {
        const model = <any>this[key];

        data.relationships[key as string] = {
          data: {
            type: model.jsonApiType,
            id: model.id,
          },
        };
      }
    });

    // Set payload data
    payload.data = data;

    return payload;
  }

  /**
   * @inheritDoc
   */
  public setAttribute(attrName: keyof this, value: any): void {
    if (this[attrName] === undefined && this.jsonApiAttributes.indexOf(attrName) === -1) {
      this.jsonApiAttributes.push(attrName);
    }

    // Set attribute
    this[attrName] = value;
  }

  /**
   * @inheritDoc
   */
  public setRelationship(relName: keyof this, models: JsonApiModelInterface | JsonApiModelInterface[] | null): void {
    if (this[relName] === undefined && this.jsonApiRelationships.indexOf(relName) === -1) {
      this.jsonApiRelationships.push(relName);
    }

    (<any>this[relName]) = models;
  }
}
